import { Box, HStack, Icon, Stack, Text } from '@chakra-ui/react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LuFileText,
  LuShieldCheck,
  LuBellRing,
  LuHouse,
  LuMapPin,
  LuScrollText,
  LuUserCog,
  LuUsers,
  LuBriefcase,
} from 'react-icons/lu';
import type { ComponentType } from 'react';
import { useMe } from '@/lib/MeContext';

type Role = 'admin' | 'asistente';
type Group = 'principal' | 'financiamiento' | 'administracion';

interface NavItem {
  to: string;
  label: string;
  icon: ComponentType;
  roles: Role[];
  group: Group;
  enabled: boolean;
}

const NAV: NavItem[] = [
  { to: '/', label: 'Inicio', icon: LuHouse, roles: ['admin', 'asistente'], group: 'principal', enabled: true },
  { to: '/clientes', label: 'Clientes', icon: LuUsers, roles: ['admin', 'asistente'], group: 'principal', enabled: true },
  { to: '/contratos', label: 'Contratos', icon: LuFileText, roles: ['admin', 'asistente'], group: 'financiamiento', enabled: true },
  { to: '/cartera', label: 'Cartera y mora', icon: LuShieldCheck, roles: ['admin', 'asistente'], group: 'financiamiento', enabled: false },
  { to: '/recordatorios', label: 'Recordatorios', icon: LuBellRing, roles: ['admin', 'asistente'], group: 'financiamiento', enabled: false },
  { to: '/proyectos', label: 'Proyectos', icon: LuMapPin, roles: ['admin'], group: 'administracion', enabled: true },
  { to: '/vendedores', label: 'Vendedores', icon: LuBriefcase, roles: ['admin'], group: 'administracion', enabled: true },
  { to: '/admin/users', label: 'Usuarios', icon: LuUserCog, roles: ['admin'], group: 'administracion', enabled: true },
  { to: '/auditoria', label: 'Auditoría', icon: LuScrollText, roles: ['admin'], group: 'administracion', enabled: true },
];

const GROUP_LABEL: Record<Group, string> = {
  principal: 'Principal',
  financiamiento: 'Financiamiento y cobranza',
  administracion: 'Administración',
};

type Props = {
  /** Fired after a nav link is activated — the mobile drawer uses it to close itself. */
  onNavigate?: () => void;
};

export function SidebarNav({ onNavigate }: Props) {
  const { me } = useMe();
  const loc = useLocation();
  const role = me?.role;

  const items = NAV.filter((n) => role && n.roles.includes(role));

  const grouped: { group: Group; items: NavItem[] }[] = [];
  for (const it of items) {
    let bucket = grouped.find((b) => b.group === it.group);
    if (!bucket) {
      bucket = { group: it.group, items: [] };
      grouped.push(bucket);
    }
    bucket.items.push(it);
  }

  return (
    <Stack as="nav" gap="3">
      {grouped.map(({ group, items }) => (
        <Stack key={group} gap="1">
          <Text
            fontSize="2xs"
            fontWeight="700"
            color="fg.muted"
            textTransform="uppercase"
            letterSpacing="0.06em"
            px="2"
          >
            {GROUP_LABEL[group]}
          </Text>
          {items.map((it) => {
            const active =
              it.to === '/'
                ? loc.pathname === '/'
                : loc.pathname === it.to || loc.pathname.startsWith(`${it.to}/`);

            const inner = (
              <HStack
                px="2.5"
                py="2"
                rounded="md"
                gap="3"
                bg={active ? 'brand.50' : 'transparent'}
                color={active ? 'brand.700' : it.enabled ? 'fg' : 'fg.subtle'}
                fontWeight={active ? '600' : '500'}
                cursor={it.enabled ? 'pointer' : 'not-allowed'}
                opacity={it.enabled ? 1 : 0.55}
                _hover={{
                  bg: it.enabled ? (active ? 'brand.50' : 'bg.subtle') : 'transparent',
                }}
                transition="background 120ms ease"
              >
                <Icon
                  as={it.icon}
                  boxSize="5"
                  color={active ? 'brand.600' : it.enabled ? 'fg.muted' : 'fg.subtle'}
                />
                <Text fontSize="sm">{it.label}</Text>
                {!it.enabled && (
                  <Box
                    ml="auto"
                    fontSize="2xs"
                    fontWeight="600"
                    px="1.5"
                    py="0.5"
                    rounded="sm"
                    bg="ink.100"
                    color="ink.600"
                  >
                    Pronto
                  </Box>
                )}
              </HStack>
            );

            if (!it.enabled) {
              return (
                <Box key={it.to} aria-disabled>
                  {inner}
                </Box>
              );
            }

            return (
              <NavLink
                key={it.to}
                to={it.to}
                style={{ textDecoration: 'none' }}
                onClick={onNavigate}
              >
                {inner}
              </NavLink>
            );
          })}
        </Stack>
      ))}
    </Stack>
  );
}
